import React, { ReactElement, ReactNode, useEffect } from 'react';

import PropTypes from 'prop-types';

import * as analytics from '@adsk/acs-web-analytics';
import { BasicButton } from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import {
  ArrowAlignRightIcon,
  ArrowAlignLeftIcon,
  RenderIconType,
} from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { ANALYTICS_TYPES, EXTERNAL_ICON_CLASSNAME } from './consts';
import { isActive } from './isActive';
import { useSideNavigation } from './SideNavigationContext';
import SideNavigationItem from './SideNavigationItem';
import SideNavigationItemLoader from './SideNavigationItemLoader';
import SideNavigationList from './SideNavigationList';

export type SideNavigationItemType = {
  key?: string;
  id?: string;
  renderIcon?: RenderIconType;
  iconUrl?: string;
  name?: string;
  loading?: boolean;
  url?: string;
  onClick?: () => void;
  'data-testid'?: string;
};

export type SideNavigationRenderItemType = {
  item: SideNavigationItemType;
  active: boolean;
  collapsed?: boolean;
};

type SideNavigationRenderListType = {
  children?: ReactNode;
};

function topItemsLoadingData(length = 10): SideNavigationItemType[] {
  return Array.from({ length }).map((_, key) => ({
    key: `SideNavigation__loader-id--${key}`,
    loading: true,
    name: '',
    url: '',
  }));
}

export type SideNavigationProps = StylableComponent<HTMLDivElement> & {
  topItems?: SideNavigationItemType[];
  bottomItems?: SideNavigationItemType[];
  selectedId?: string;
  renderList?: (props: SideNavigationRenderListType) => ReactElement;
  renderItem?: (props: SideNavigationRenderItemType) => ReactElement;
};
/**
 * Navigation component, rendering on the left side of page content.
 */
const SideNavigation = React.forwardRef<HTMLDivElement, SideNavigationProps>(
  (
    {
      style,
      className,
      topItems,
      bottomItems,
      selectedId,
      // TODO: Convert this to be used as a render prop instead of component (breaking change)
      // eslint-disable-next-line @adsk/react-render-props/render-props-as-components, @adsk/react-render-props/component-to-render-prop
      renderList: List = SideNavigationList,
      // TODO: Convert this to be used as a render prop instead of component (breaking change)
      // eslint-disable-next-line @adsk/react-render-props/render-props-as-components, @adsk/react-render-props/component-to-render-prop
      renderItem: Item = SideNavigationItem,
      ...props
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const { collapsed, setCollapsed } = useSideNavigation();
    const onToggle = () => {
      analytics.track(ANALYTICS_TYPES.SIDENAVIGATION_EXPANDCOLLAPSE_CLICK, {
        is_collapsed: !collapsed,
      });
      setCollapsed((val) => !val);
    };

    const onClickTool = (item: SideNavigationItemType) => {
      analytics.track(ANALYTICS_TYPES.SIDENAVIGATION_TOOL_CLICK, {
        tool_id: item.key,
        tool_name: item.name,
      });
    };

    useEffect(
      () =>
        analytics.track(ANALYTICS_TYPES.SIDENAVIGATION_VIEW, {
          package_name: 'SideNavigation',
        }),
      [],
    );

    const IconComponent = collapsed ? ArrowAlignRightIcon : ArrowAlignLeftIcon;
    return (
      <div
        {...props}
        className={className}
        ref={ref}
        css={[
          {
            background: theme.colors.charcoal050,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          },
          style,
        ]}
      >
        {topItems !== undefined && (
          <div css={[{ overflow: 'auto' }]}>
            <List>
              {(topItems.length ? topItems : topItemsLoadingData()).map(
                (item, index) => (
                  <Item
                    key={item.id || item.key}
                    item={item}
                    active={isActive(item, selectedId)}
                    collapsed={collapsed}
                    data-testid={item['data-testid']}
                    onClick={() => onClickTool(item)}
                  >
                    {item.loading && (
                      <SideNavigationItemLoader
                        collapsed={collapsed}
                        index={index}
                      />
                    )}
                    {!item.loading && (
                      <Tooltip
                        placement={Tooltip.PLACEMENTS.RIGHT}
                        content={collapsed ? item.name : ''}
                        delayShow={0}
                        shouldWrapChildren={false}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          {item.iconUrl ? (
                            <span
                              className={EXTERNAL_ICON_CLASSNAME}
                              css={[
                                {
                                  maskImage: `url(${item.iconUrl})`,
                                  backgroundColor: isActive(item, selectedId)
                                    ? theme.colors.adskBlue500
                                    : theme.colors.charcoal700,
                                  width: 24,
                                  height: 24,
                                },
                              ]}
                            />
                          ) : (
                            item.renderIcon?.({
                              size: 24,
                            })
                          )}
                          {!collapsed && (
                            <span
                              css={[
                                {
                                  marginLeft: 12,
                                  width: 160,
                                },
                              ]}
                            >
                              <OverflowTooltip>{item.name}</OverflowTooltip>
                            </span>
                          )}
                        </div>
                      </Tooltip>
                    )}
                  </Item>
                ),
              )}
            </List>
          </div>
        )}

        <div
          css={[
            {
              flexShrink: 0,
              paddingBottom: 4,
            },
          ]}
        >
          {topItems && (
            <Divider
              style={{
                width: 'auto',
                margin: '0 8px',
              }}
            />
          )}
          {bottomItems && (
            <List>
              {bottomItems.map((item) => (
                <Item
                  key={item.id || item.key}
                  item={item}
                  collapsed={collapsed}
                  active={isActive(item, selectedId)}
                  onClick={() => onClickTool(item)}
                >
                  <Tooltip
                    placement={Tooltip.PLACEMENTS.RIGHT}
                    content={collapsed ? item.name : ''}
                    delayShow={0}
                    shouldWrapChildren={false}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {item.iconUrl ? (
                        <span
                          className={EXTERNAL_ICON_CLASSNAME}
                          css={[
                            {
                              maskImage: `url(${item.iconUrl})`,
                              backgroundColor: isActive(item, selectedId)
                                ? theme.colors.adskBlue500
                                : theme.colors.charcoal700,
                              width: 24,
                              height: 24,
                            },
                          ]}
                        />
                      ) : (
                        item.renderIcon?.({
                          size: 24,
                        })
                      )}

                      {!collapsed && (
                        <span
                          css={[
                            theme.typography.ellipsis,
                            {
                              marginLeft: 12,
                              width: 160,
                            },
                          ]}
                        >
                          {item.name}
                        </span>
                      )}
                    </div>
                  </Tooltip>
                </Item>
              ))}
            </List>
          )}

          <Tooltip
            placement={Tooltip.PLACEMENTS.RIGHT}
            content={
              collapsed ? T.translate('EXPAND') : T.translate('COLLAPSE')
            }
            shouldWrapChildren={false}
            delayShow={0}
          >
            <div style={{ display: 'inline-block' }}>
              <BasicButton
                onClick={onToggle}
                style={[
                  theme.typography.bodyMedium,
                  {
                    padding: 6,
                    margin: '4px 6px',
                    color: theme.colors.charcoal700,
                    ':hover': {
                      background: theme.colors.charcoal100,
                    },
                    ':active': {
                      background: theme.colors.charcoal200,
                    },
                  },
                ]}
              >
                <IconComponent size={24} />
              </BasicButton>
            </div>
          </Tooltip>
        </div>
      </div>
    );
  },
);

SideNavigation.displayName = 'SideNavigation';

SideNavigation.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Links to display above divider; main links */
  topItems: PropTypes.array,
  /** Links to display below divider; footer links */
  bottomItems: PropTypes.array,
  /** The currently selected item Id (or key) */
  selectedId: PropTypes.string,
  /** Optional override for rendering custom navigation list */
  renderList: PropTypes.func,
  /** Optional override for rendering custom navigation items */
  renderItem: PropTypes.func,
};

export default SideNavigation;
